import {
  formatFollowResponse,
  GetUserInfo,
  HandleFollowTopic,
  HandleUnFollowTopic,
} from "@helper/baasHelper";
import useGetFollowedTopic from "@hooks/useGetFollowedTopic";
import { useCallback, useEffect, useRef, useState } from "react";

type UseFollowTagsReturn = {
  visitorId: string;
  followedTags: string[];
  isFollowing: (topicId: string) => boolean;
  toggleFollow: (topicId: string, topicTitle: string) => Promise<void>;
  loadingTopicId: string | null;
  showToast: boolean;
};

type UseFollowTagsProps = {
  onRequireLogin?: () => void;
};

export default function useFollowTags({
  onRequireLogin,
}: UseFollowTagsProps): UseFollowTagsReturn {
  const [followedTags, setFollowedTags] = useState<string[]>([]);
  const [loadingTopicId, setLoadingTopicId] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);

  const requestInFlight = useRef(false);

  const currentUser = GetUserInfo();
  const visitorId = currentUser.visitorId;

  const { topicResponse, getFollowedTopic } = useGetFollowedTopic(visitorId);

  // ================= INIT =================
  useEffect(() => {
    if (visitorId) {
      void getFollowedTopic();
    }
  }, [getFollowedTopic, visitorId]);

  // ================= SYNC RESPONSE =================
  useEffect(() => {
    const tags = formatFollowResponse(topicResponse);
    if (tags.length) {
      setFollowedTags(tags);
    }
  }, [topicResponse]);

  // ================= HELPERS =================
  const isFollowing = useCallback(
    (topicId: string) => followedTags.includes(topicId),
    [followedTags],
  );

  // ================= TOGGLE =================
  const toggleFollow = useCallback(
    async (topicId: string, topicTitle: string) => {
      if (!visitorId) {
        onRequireLogin?.();
        return;
      }

      if (!topicId || requestInFlight.current) return; // prevent race condition

      requestInFlight.current = true;
      setLoadingTopicId(topicId);

      try {
        if (!isFollowing(topicId)) {
          await HandleFollowTopic(topicId, visitorId, topicTitle, () => {});
          setFollowedTags((prev) => [...new Set([...prev, topicId])]);

          // trigger toast only on follow
          setShowToast(true);
        } else {
          await HandleUnFollowTopic(topicId, visitorId, topicTitle, () => {});
          setFollowedTags((prev) => prev.filter((id) => id !== topicId));
        }
      } catch (err) {
        console.error("Follow toggle failed", err);
      } finally {
        requestInFlight.current = false;
        setLoadingTopicId(null);
      }
    },
    [visitorId, isFollowing, onRequireLogin],
  );

  // reset toast trigger (so it can fire again)
  useEffect(() => {
    if (showToast) {
      const t = setTimeout(() => {
        setShowToast(false);
      }, 0);
      return () => {
        clearTimeout(t);
      };
    }
  }, [showToast]);

  return {
    visitorId,
    followedTags,
    isFollowing,
    toggleFollow,
    loadingTopicId,
    showToast,
  };
}
